import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from './common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { FaRupeeSign } from 'react-icons/fa';

const { FiCopy, FiCheck, FiRefreshCw, FiPercent, FiCalculator } = FiIcons;

const StopLossCalculator = () => {
  const [premium, setPremium] = useState('');
  const [customPct, setCustomPct] = useState('');
  const [copied, setCopied] = useState(null);

  const percentages = [
    1, 2, 3, 5, 10, 15, 20, 25, 30, 35, 40, 45, 
    50, 55, 60, 65, 70, 75, 80, 85, 90, 95, 100
  ];
  
  const highlighted = [1, 5, 10, 20, 50, 100];

  const calculate = (price, pct) => {
    if (!price || isNaN(price)) return { stopLoss: '-', target: '-' };
    const val = parseFloat(price);
    const change = val * (pct / 100);
    return {
      stopLoss: (val - change).toFixed(2),
      target: (val + change).toFixed(2)
    };
  };

  const handleCopy = (text, id) => {
    if (text === '-') return;
    navigator.clipboard.writeText(text);
    setCopied(id);
    setTimeout(() => setCopied(null), 1500);
  };

  const handleReset = () => {
    setPremium('');
    setCustomPct('');
  };

  return (
    <div className="w-full max-w-2xl mx-auto px-4">
      <motion.div 
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-black/60 backdrop-blur-xl border border-white/10 rounded-[2rem] overflow-hidden shadow-[0_0_50px_rgba(0,0,0,0.5)]"
      >
        {/* Header */}
        <div className="p-6 md:p-8 border-b border-white/10 bg-gradient-to-r from-red-900/30 to-transparent relative overflow-hidden">
          <div className="absolute top-0 right-0 p-8 opacity-10">
            <SafeIcon icon={FiCalculator} className="w-32 h-32 text-red-500" />
          </div>
          <h2 className="text-2xl md:text-3xl font-black text-white uppercase tracking-tighter mb-2 relative z-10 drop-shadow-lg">
            RocketAlgo <span className="text-red-500 glow-text-red">StopLoss</span> Calculator
          </h2>
          <p className="text-white/60 text-sm font-medium tracking-wide relative z-10">
            Calculate your risk and reward targets instantly directly from Premium Price.
          </p>
        </div>

        {/* Input Section */}
        <div className="p-6 md:p-8 space-y-6">
          <div className="relative group">
            <label className="block text-xs font-bold text-red-500 uppercase tracking-widest mb-2 ml-1 glow-text-red">
              Premium Price
            </label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                <SafeIcon icon={FaRupeeSign} className="text-white/40 w-4 h-4 group-focus-within:text-red-500 transition-colors" />
              </div>
              <input
                type="number"
                value={premium}
                onChange={(e) => setPremium(e.target.value)}
                placeholder="0.00"
                className="w-full bg-black/40 border border-white/10 rounded-xl py-4 pl-12 pr-12 text-2xl md:text-3xl font-black text-white placeholder-white/20 focus:outline-none focus:border-red-500/50 focus:ring-1 focus:ring-red-500/20 focus:shadow-[0_0_20px_rgba(220,38,38,0.2)] transition-all"
              />
              {premium && (
                <button 
                  onClick={handleReset}
                  className="absolute inset-y-0 right-0 pr-4 flex items-center text-white/40 hover:text-white transition-colors hover:scale-110"
                >
                  <SafeIcon icon={FiRefreshCw} className="w-5 h-5" />
                </button>
              )}
            </div>
          </div>

          {/* Custom Percentage */}
          <div className="relative">
            <label className="block text-[10px] font-bold text-white/50 uppercase tracking-widest mb-2 ml-1">
              Custom Percentage
            </label>
            <div className="relative max-w-[200px]">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <SafeIcon icon={FiPercent} className="text-white/40 w-3.5 h-3.5" />
              </div>
              <input
                type="number"
                value={customPct}
                onChange={(e) => setCustomPct(e.target.value)}
                placeholder="Custom %"
                className="w-full bg-black/30 border border-white/10 rounded-lg py-2 pl-9 pr-3 text-sm font-bold text-white placeholder-white/20 focus:outline-none focus:border-red-500/50 focus:shadow-[0_0_15px_rgba(220,38,38,0.1)] transition-all"
              />
            </div>
          </div>

          {/* Results Table */}
          <div className="border border-white/10 rounded-xl overflow-hidden bg-black/30 backdrop-blur-sm">
            <div className="grid grid-cols-3 bg-white/5 border-b border-white/10 p-3 text-[10px] md:text-xs font-black uppercase tracking-widest text-white/40">
              <div className="text-center text-red-500 glow-text-red">Stop Loss</div>
              <div className="text-center">Percentage</div>
              <div className="text-center text-green-500 glow-text-green">Target</div>
            </div>
            
            <div className="max-h-[400px] overflow-y-auto custom-scrollbar">
              <AnimatePresence>
                {/* Custom Row */}
                {customPct && (
                  <motion.div 
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    exit={{ opacity: 0, height: 0 }}
                    className="grid grid-cols-3 p-3 md:p-4 border-b border-red-500/30 bg-red-500/10 items-center group hover:bg-red-500/15 transition-colors"
                  >
                    {(() => {
                      const { stopLoss, target } = calculate(premium, customPct);
                      return (
                        <>
                          <motion.div 
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                            className="text-center font-mono font-bold text-red-500 cursor-pointer hover:text-white flex justify-center items-center gap-2 glow-text-red"
                            onClick={() => handleCopy(stopLoss, 'custom-sl')}
                          >
                            {stopLoss}
                            {copied === 'custom-sl' ? <SafeIcon icon={FiCheck} className="w-3 h-3 text-green-500" /> : <SafeIcon icon={FiCopy} className="w-3 h-3 opacity-0 group-hover:opacity-50" />}
                          </motion.div>
                          
                          <div className="text-center font-black text-white">{customPct}%</div>
                          
                          <motion.div 
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                            className="text-center font-mono font-bold text-green-500 cursor-pointer hover:text-white flex justify-center items-center gap-2 glow-text-green"
                            onClick={() => handleCopy(target, 'custom-target')}
                          >
                            {target}
                            {copied === 'custom-target' ? <SafeIcon icon={FiCheck} className="w-3 h-3 text-green-500" /> : <SafeIcon icon={FiCopy} className="w-3 h-3 opacity-0 group-hover:opacity-50" />}
                          </motion.div>
                        </>
                      );
                    })()}
                  </motion.div>
                )}

                {/* Standard Rows */}
                {percentages.map((pct) => {
                  const isHighlighted = highlighted.includes(pct);
                  const { stopLoss, target } = calculate(premium, pct);
                  
                  return (
                    <motion.div 
                      key={pct}
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      className={`grid grid-cols-3 p-3 md:p-3.5 border-b border-white/5 items-center group transition-all ${
                        isHighlighted ? 'bg-white/5 hover:bg-white/10' : 'hover:bg-white/[0.03]'
                      }`}
                    >
                      <motion.div 
                        whileHover={{ scale: 1.05, color: '#ffaaaa' }}
                        whileTap={{ scale: 0.95 }}
                        className="text-center font-mono font-bold text-red-500 cursor-pointer flex justify-center items-center gap-2 relative transition-colors"
                        onClick={() => handleCopy(stopLoss, `sl-${pct}`)}
                      >
                        {stopLoss}
                        {copied === `sl-${pct}` ? (
                          <SafeIcon icon={FiCheck} className="w-3 h-3 text-green-500 absolute -right-1" />
                        ) : (
                          <SafeIcon icon={FiCopy} className="w-3 h-3 opacity-0 group-hover:opacity-40 absolute -right-1" />
                        )}
                      </motion.div>

                      <div className={`text-center font-bold ${isHighlighted ? 'text-white' : 'text-white/40'}`}>
                        {pct}%
                      </div>
                      
                      <motion.div 
                        whileHover={{ scale: 1.05, color: '#aaffaa' }}
                        whileTap={{ scale: 0.95 }}
                        className="text-center font-mono font-bold text-green-500 cursor-pointer flex justify-center items-center gap-2 relative transition-colors"
                        onClick={() => handleCopy(target, `tg-${pct}`)}
                      >
                        {target}
                        {copied === `tg-${pct}` ? (
                          <SafeIcon icon={FiCheck} className="w-3 h-3 text-green-500 absolute -right-1" />
                        ) : (
                          <SafeIcon icon={FiCopy} className="w-3 h-3 opacity-0 group-hover:opacity-40 absolute -right-1" />
                        )}
                      </motion.div>
                    </motion.div>
                  );
                })}
              </AnimatePresence>
            </div>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default StopLossCalculator;